import React, { useState, useEffect } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { RFC } from 'state/StateTypes';
import * as assetTypes from 'state/assets/AssetTypes';
import { InputChange, SelectChange, TextAreaChange, FormSubmission, Error, ErrorCodes } from '../../types';
import { createAsset } from 'state/assets/AssetActions';
import styled from 'styled-components';
import TierSelect from 'components/TierSelect';
import TextArea from 'components/TextArea';
import Form from 'components/Form';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import ErrorMessage from 'components/ErrorMessage';
import FormHeader from 'components/FormHeader';
import Legend from 'components/Legend';
import Button from 'components/Button';
import { SubscriptionTiers } from 'state/subscription/SubscriptionTypes';
import Input from 'components/Form/Input';
import BackButton from 'components/BackButton';
import { validateBytes, presentErrorBasedOnCode } from 'utils';
import isAuthenticated from 'hooks/isAuthenticated';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'state/store';
import { selectSelectedAsset } from 'state/assets/AssetSelectors';

const initialFormState: assetTypes.CreateAssetRequest = {
   name: '',
   tier: undefined,
   bytes: [],
   description: '',
   type: assetTypes.AssetTypes.Sensation,
};

const SensationCreate: RFC<RouteComponentProps> = () => {
   const [form, setForm] = useState<assetTypes.CreateAssetRequest>(initialFormState);
   const createdSensation: assetTypes.Asset | null = useSelector((state: AppState) => selectSelectedAsset(state));
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const [loading, setLoading] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);
   const [formComplete, setFormComplete] = useState<boolean>(false);
   const [byteStrings, setByteStrings] = useState<string>('');
   const [byteError, setByteError] = useState<boolean>(false);
   const dispatch = useDispatch();

   useEffect(() => {
      if (!!form.name && !!form.tier && !!form.description && !!byteStrings) {
         setFormComplete(true);
      } else {
         setFormComplete(false);
      }
   }, [form, byteStrings]);

   const handleInput = (e: InputChange | SelectChange | TextAreaChange): void => {
      e.persist();
      setForm(form => ({ ...form, [e.target.name]: e.target.value }));
   };

   useEffect(() => {
      if (createdSensation) {
         setLoading(false);
         //navigate user to details page for created sensation
         navigate(`/sensations/${createdSensation._id}`);
      }
   }, [createdSensation]);

   const handleSubmit = async (e: FormSubmission): Promise<void> => {
      e.preventDefault();
      setLoading(true);
      const bytes: number[] | null = validateBytes(byteStrings, 240);
      if (authenticated && bytes) {
         try {
            await dispatch(createAsset({ ...form, bytes }));
            byteError && setByteError(false);
         } catch (error) {
            //if error results from missing token it will not have a json method
            error = error.json ? await error.json() : error;
            setError(error);
         }
      } else {
         setByteError(true);
      }
      setLoading(false);
   };

   return (
      <Container margin="0 auto">
         <BackButton to="/sensations" />
         <FormContainer>
            <Header>CREATE SENSATION</Header>
            <Legend>
               Fields with <span>*</span> are required
            </Legend>
            <Form onSubmit={handleSubmit}>
               <Input
                  label="Name"
                  value={form.name}
                  name="name"
                  onChange={handleInput}
                  margin="1em auto"
                  size="full"
                  errorMessage="Sensation name is required"
                  type="text"
                  required={true}
               />
               <TierSelect label="Tier" name="tier" value={form.tier} onChange={handleInput} required={true}>
                  <option value={SubscriptionTiers.Free}>{SubscriptionTiers.Free}</option>
                  <option value={SubscriptionTiers.Paid}>{SubscriptionTiers.Paid}</option>
               </TierSelect>
               <TextArea
                  placeholder="Sensation description"
                  label="Description"
                  value={form.description}
                  name="description"
                  onChange={handleInput}
                  required={true}
               />
               <ToolTip>Please enter bytes as comma separated numbers (128, 255, etc)</ToolTip>
               <TextArea
                  placeholder="Sensation bytes"
                  label="Bytes"
                  value={byteStrings}
                  name="bytes"
                  onChange={(e: TextAreaChange) => setByteStrings(e.target.value)}
                  required={true}
                  errorMessage={byteError && 'Please ensure there are 240 bytes between 0-255, whole numbers or valid hexadecimals'}
               />
               <Button isLoading={loading} disabled={!formComplete || loading}>
                  CREATE
               </Button>
            </Form>
            {error && <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>}
         </FormContainer>
      </Container>
   );
};

export default SensationCreate;

const Header = styled(FormHeader)`
   margin: 0 0 1em 0;
`;

const ToolTip = styled.small`
   color: ${({ theme }) => theme.primaryGrey};
   display: inline-block;
`;
